import { cn } from '@seen/ui'
import { IconChevronRight } from '@tabler/icons-react'
import { Fragment, type JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import type {
  ModalStackHandle,
  ModalStackLayer,
} from '#src/components/core/dialog/ModalStackContext.ts'

// Informational (not navigable) breadcrumb trail: the root label plus each
// layer's breadcrumb, deepest bold. Backward navigation is the layer's own
// footer Back/Cancel, not a breadcrumb click.
function Breadcrumbs({
  rootLabel,
  path,
}: {
  rootLabel: string
  path: ModalStackLayer[]
}): JSX.Element {
  const crumbs = [rootLabel, ...path.map((layer) => layer.breadcrumb)]
  const lastIndex = crumbs.length - 1
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex flex-wrap items-center gap-1.5 text-xs"
    >
      {crumbs.map((label, index) => (
        // oxlint-disable-next-line react/no-array-index-key -- crumb labels can repeat; position is the stable identity
        <Fragment key={index}>
          {index > 0 && (
            <IconChevronRight className="size-3 shrink-0 text-muted-foreground" />
          )}
          <span
            className={cn(
              index === lastIndex
                ? 'font-medium text-foreground'
                : 'text-foreground-secondary'
            )}
          >
            {label}
          </span>
        </Fragment>
      ))}
    </nav>
  )
}

// Header for one stack layer: its title/subtitle plus the breadcrumb trail from
// the root down to it.
function ModalStackHeader({
  layer,
  path,
  rootLabel,
}: {
  layer: ModalStackLayer
  path: ModalStackLayer[]
  rootLabel: string
}): JSX.Element {
  return (
    <DialogHeader className="flex-col gap-2 py-4">
      <div className="flex flex-col gap-0.5">
        <DialogTitle>{layer.title}</DialogTitle>
        {layer.subtitle && (
          <DialogDescription>{layer.subtitle}</DialogDescription>
        )}
      </div>
      <Breadcrumbs rootLabel={rootLabel} path={path} />
    </DialogHeader>
  )
}

// Deeper layers sit higher and slightly narrower than the one below, so each
// parent card peeks out below/around the active one — the stack stays visible.
const DEPTH_PLACEMENT = [
  'mt-[10dvh] max-w-184',
  'mt-[7dvh] max-w-176',
  'mt-[4dvh] max-w-2xl',
]

function depthPlacement(depth: number): string {
  // depth is always ≥ 1 (index + 1), so the clamped lookup is always in bounds.
  return DEPTH_PLACEMENT[Math.min(depth, DEPTH_PLACEMENT.length) - 1]
}

// Renders each stack layer as its own dialog, stacked over the base (and each
// other). All parent dialogs stay mounted — so their form state survives — and
// show through a lightened scrim behind the active layer. Closing a layer
// (footer Back, Esc, or the ✕) pops back to its parent.
export function ModalStackLayers({
  layers,
  handle,
  rootLabel,
}: {
  layers: ModalStackLayer[]
  handle: ModalStackHandle
  // Label for the first breadcrumb (the base content, e.g. "Send fax").
  rootLabel: string
}): JSX.Element {
  return (
    <>
      {layers.map((layer, index) => (
        <Dialog
          key={layer.key}
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              handle.popTo(index - 1)
            }
          }}
        >
          <DialogDisplay
            // Lighter than the default scrim so the parent card stays visible.
            overlayClassName="bg-overlay/40"
            className={depthPlacement(index + 1)}
          >
            <ModalStackHeader
              layer={layer}
              path={layers.slice(0, index + 1)}
              rootLabel={rootLabel}
            />
            <DialogContent className="pt-0">{layer.render()}</DialogContent>
          </DialogDisplay>
        </Dialog>
      ))}
    </>
  )
}
